import React, { useState, useEffect } from 'react';
import { FileText, ArrowUpCircle, ArrowDownCircle, TrendingUp, TrendingDown, Edit2, CheckSquare, Users, Tag, DollarSign, Bell, Clock } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';

const HistoryTab = ({ details }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadHistory();
  }, [details?.user?.id]);
  
  const loadHistory = async () => {
    if (!details?.user?.id) return;
    
    setLoading(true);
    try {
      const res = await adminAPI.getClientHistory(details.user.id, 90);
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to load history:', err);
      buildLocalHistory();
    } finally {
      setLoading(false);
    }
  };
  
  const buildLocalHistory = () => {
    const localEvents = [];
    
    (details?.notes || []).forEach(note => {
      localEvents.push({
        type: 'note',
        title: 'Заметка',
        description: note.note,
        date: note.created_at,
        actor: 'Менеджер'
      });
    });
    
    (details?.deposits || []).forEach(dep => {
      localEvents.push({
        type: 'deposit',
        title: `Депозит ${dep.status}`,
        description: `+$${dep.amount} ${dep.currency}`,
        date: dep.created_at,
        actor: dep.status === 'pending' ? 'Клиент' : 'Админ'
      });
    });
    
    (details?.withdrawals || []).forEach(wit => {
      localEvents.push({
        type: 'withdrawal',
        title: `Вывод ${wit.status}`,
        description: `-$${wit.amount} ${wit.currency}`,
        date: wit.created_at,
        actor: wit.status === 'pending' ? 'Клиент' : 'Админ'
      });
    });
    
    (details?.positions || []).forEach(pos => {
      if (pos.modification_history) {
        pos.modification_history.forEach(mod => {
          localEvents.push({
            type: 'position_edit',
            title: `Позиция ${pos.symbol} изменена`,
            description: `${mod.field}: ${mod.old_value} → ${mod.new_value}`,
            date: mod.modified_at,
            actor: 'Админ'
          });
        });
      }
      localEvents.push({
        type: pos.status === 'open' ? 'position_open' : 'position_close',
        title: `Позиция ${pos.symbol} ${pos.status === 'open' ? 'открыта' : 'закрыта'}`,
        description: `${pos.side?.toUpperCase()} ${pos.leverage}x @ $${pos.entry_price}`,
        date: pos.opened_at || pos.closed_at,
        actor: 'Клиент'
      });
    });
    
    localEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
    setEvents(localEvents);
  };
  
  const getEventIcon = (type) => {
    const iconMap = {
      'note': FileText,
      'deposit': ArrowUpCircle,
      'withdrawal': ArrowDownCircle,
      'position_open': TrendingUp,
      'position_close': TrendingDown,
      'position_edit': Edit2,
      'status_change': CheckSquare,
      'assign_manager': Users,
      'tag_add': Tag,
      'tag_remove': Tag,
      'deposit_approve': DollarSign,
      'notification': Bell
    };
    return iconMap[type] || Clock;
  };
  
  const getEventColor = (type) => {
    const colorMap = {
      'note': '#8b5cf6',
      'deposit': COLORS.success,
      'withdrawal': COLORS.danger,
      'position_open': COLORS.success,
      'position_close': COLORS.danger,
      'position_edit': COLORS.primary,
      'status_change': '#3b82f6',
      'assign_manager': '#06b6d4',
      'tag_add': '#14b8a6',
      'tag_remove': '#6b7280',
      'deposit_approve': COLORS.success,
      'notification': '#3b82f6'
    };
    return colorMap[type] || COLORS.textSecondary;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>Загрузка истории...</div>;
  }

  return (
    <div>
      {events.length > 0 ? (
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute',
            left: '15px',
            top: '20px',
            bottom: '20px',
            width: '2px',
            backgroundColor: COLORS.border,
          }} />

          {events.map((event, i) => {
            const Icon = getEventIcon(event.type);
            const color = getEventColor(event.type);
            return (
              <div key={i} style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '16px',
                position: 'relative',
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: `${color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  zIndex: 1,
                }}>
                  <Icon size={16} color={color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <span style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>{event.title}</span>
                    <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>{formatDate(event.date)}</span>
                  </div>
                  <p style={{ color: COLORS.textSecondary, fontSize: '12px', margin: 0, lineHeight: '1.4' }}>
                    {event.description}
                  </p>
                  {event.actor && (
                    <span style={{ color: COLORS.textSecondary, fontSize: '10px', fontStyle: 'italic' }}>
                      — {event.actor}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ color: COLORS.textSecondary, fontSize: '13px', textAlign: 'center', padding: '20px' }}>
          История пуста
        </div>
      )}
    </div>
  );
};

export default HistoryTab;
